import { DragEndEvent, DragStartEvent } from "@dnd-kit/core"
import { MouseEvent, MouseEventHandler, ReactNode } from "react"

import { ListKeyboardNavigation } from "../../../../hooks/useListKeyboardNavigation"
import { ColumnReorderFn } from "../ColumnDnd/hooks/useColumnOrder"
import { TableCellHeight } from "../TableCell"

export const HeaderGroupCellType = {
  Group: "group",
  Empty: "empty",
} as const
export type HeaderGroupCellType =
  (typeof HeaderGroupCellType)[keyof typeof HeaderGroupCellType]

export const HeaderCellType = {
  Header: "header",
  Checkbox: "checkbox",
  Empty: "empty",
} as const
export type HeaderCellType =
  (typeof HeaderCellType)[keyof typeof HeaderCellType]

export const BodyCellType = {
  Data: "data",
  Checkbox: "checkbox",
  Empty: "empty",
  RadioButton: "radio-button",
  DragHandle: "drag-handle",
} as const
export type BodyCellType = (typeof BodyCellType)[keyof typeof BodyCellType]

interface HeaderGroupRowBaseCell {
  key: string
  colSpan: number
}
export interface HeaderGroupRowGroupCell extends HeaderGroupRowBaseCell {
  type: typeof HeaderGroupCellType.Group
  title: ReactNode
  accessory?: ReactNode
  isExpandable: boolean
  isExpanded: boolean
  onToggleExpanded: () => void
}

export interface HeaderGroupRowEmptyCell extends HeaderGroupRowBaseCell {
  type: typeof HeaderGroupCellType.Empty
}

export type HeaderGroupRowCell =
  | HeaderGroupRowGroupCell
  | HeaderGroupRowEmptyCell

interface HeaderRowBaseCell {
  key: string
  width: number
}

export interface HeaderRowHeaderCell extends HeaderRowBaseCell {
  type: typeof HeaderCellType.Header
  content: ReactNode
  onToggleColumnHideShow?: () => void
}

export interface HeaderRowCheckboxCell extends HeaderRowBaseCell {
  type: typeof HeaderCellType.Checkbox
  isChecked: boolean
  onChangeIsChecked: (isChecked: boolean) => void
}

export interface HeaderRowEmptyCell extends HeaderRowBaseCell {
  type: typeof HeaderCellType.Empty
}

export type HeaderRowCell =
  | HeaderRowHeaderCell
  | HeaderRowCheckboxCell
  | HeaderRowEmptyCell

export interface BodyRowDataCell {
  key: string
  type: typeof BodyCellType.Data
  content: ReactNode
  testId?: string
}

export interface BodyRowCheckboxCell {
  key: string
  type: typeof BodyCellType.Checkbox
  isChecked: boolean
  onChangeIsChecked: ({
    isChecked,
    isShiftPressed,
  }: {
    isChecked: boolean
    isShiftPressed: boolean
  }) => void
}

export interface BodyRowRadioButtonCell {
  key: string
  type: typeof BodyCellType.RadioButton
  isChecked: boolean
  onChangeIsChecked: (isChecked: boolean) => void
}

export interface BodyRowDragHandleCell {
  key: string
  type: typeof BodyCellType.DragHandle
}

export type BodyRowCell =
  | BodyRowDataCell
  | BodyRowCheckboxCell
  | BodyRowRadioButtonCell
  | BodyRowDragHandleCell

export interface HeaderGroupRow {
  cells: HeaderGroupRowCell[]
}

export interface HeaderRow {
  cells: HeaderRowCell[]
}

export interface BodyRow {
  key: string
  cells: BodyRowCell[]
  onClick?: MouseEventHandler<HTMLTableRowElement>
}

export type StartColumnResizeCallback = (
  key: string,
  e: MouseEvent<HTMLDivElement>
) => void

export interface TableHeader {
  groupRow: HeaderGroupRow | null
  row: HeaderRow
  onStartColumnResize?: StartColumnResizeCallback
  onReorder?: ColumnReorderFn
  enableColumnReordering?: boolean
  hideColumnBorders?: boolean
}

export interface TableBody {
  rows: BodyRow[]
  enableRowHighlighting?: boolean
  enableRowReordering?: boolean
  emptyPlaceholderMessage?: string
  hideColumnBorders?: boolean
}

export interface TableTesting {
  tableTestId?: string
}

export interface TableRowReordering {
  rowKeys: string[]
  draggingRowKey?: string
  onDragStart: (event: DragStartEvent) => void
  onDragEnd: (event: DragEndEvent) => void
}

export interface TableProps {
  cellHeight: TableCellHeight
  header: TableHeader
  body: TableBody
  isError?: boolean
  isLoading?: boolean
  onLastRowInView?: () => void
  testing?: TableTesting
  rowReordering?: TableRowReordering
  keyboardNavigation?: ListKeyboardNavigation<HTMLTableSectionElement>
  hideColumnBorders?: boolean
  isHeaderSticky?: boolean
}
